<template>
  <div class="question-wrong-trend">
    <el-card class="chart-card full-width">
      <div slot="header" class="header">
        <div class="title">题目错误趋势</div>
        <div class="operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExport" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
          </el-tooltip>
        </div>
      </div>
      <div class="chart-container" ref="chart">
        <div class="chart-content">
          <div class="y-axis-label">题目数量</div>
          <div class="y-axis-label right">订正率</div>
        </div>
      </div>
    </el-card>

    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog vertical-center-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose"
    >
      <div slot="title" class="dialog-header">
        <span>错题趋势分布</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import { getErrorTagInlineStyle } from '@/constants/errorTypes'

export default {
  name: 'QuestionWrongTrend',
  data() {
    return {
      chart: null,
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      chartData: {
        months: [],          // x 轴：年份/月份
        correct: [],         // 正确数量（用于堆叠最底层）
        undefined: [],       // 未定义错误数量
        errorCodes: [],      // 实际错误类型列表（后端返回）
        errorNames: {},      // error_code => error_name
        errors: {},          // 每种错误类型逐月数据
        fixRates: []         // 订正率（0-1）
      },
      // 颜色：与全局错误类型配色一致
      baseColors: {
        '正确': '#67C23A',
        '未定义': '#D9D9D9'
      }
    }
  },
  mounted() {
    this.initChart()
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) this.chart.dispose()
    if (this.fullscreenChart) this.fullscreenChart.dispose()
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    initChart() {
      this.$nextTick(() => {
        const el = this.$refs.chart
        if (!el) return
        this.chart = echarts.init(el)
        setTimeout(() => { this.chart && this.chart.resize() }, 0)
      })
    },
    resizeChart() {
      this.chart?.resize()
    },
    forceResize() {
      try {
        if (!this.chart) this.initChart()
        this.updateChart()
        this.chart && this.chart.resize()
      } catch (e) {}
    },
    async fetchData() {
      try {
        // 期望后端提供的数据格式：
        // {
        //   months: ['2024-01','2024-02',...],
        //   correct: [..],
        //   undefined: [..],
        //   errors: { '1-xxx': [...], '2-xxx': [...], ..., '9-xxx': [...] },
        //   fix_rates: [0.32, 0.41, ...]  // 0~1
        // }
        const res = await request.get('/api/analysis/question/wrong_trend')
        const data = res?.data || {}
        this.chartData = {
          months: data.months || [],
          correct: data.correct || [],
          undefined: data.undefined || [],
          errorCodes: data.error_codes || [],
          errorNames: data.error_names || {},
          errors: data.errors || {},
          fixRates: data.fix_rates || []
        }
        this.updateChart()
      } catch (error) {
        // 后端未实现时兜底为空图
        this.chartData = { months: [], correct: [], undefined: [], errorStacks: {}, fixRates: [] }
        this.updateChart()
      }
    },
    buildSeries() {
      const series = []
      // 堆叠柱：先“正确”“未定义”，再动态错误类型（后端顺序）
      series.push({
        name: '正确',
        type: 'bar',
        stack: 'total',
        barMaxWidth: 50,
        itemStyle: { color: this.baseColors['正确'], borderRadius: [4, 4, 0, 0] },
        emphasis: { focus: 'series' },
        data: this.chartData.correct || []
      })
      series.push({
        name: '未定义',
        type: 'bar',
        stack: 'total',
        barMaxWidth: 50,
        itemStyle: { color: this.baseColors['未定义'], borderRadius: [4, 4, 0, 0] },
        emphasis: { focus: 'series' },
        data: this.chartData.undefined || []
      })
      for (const code of (this.chartData.errorCodes || [])) {
        const style = getErrorTagInlineStyle(code) || {}
        const color = style.backgroundColor || '#1E90FF'
        series.push({
          name: this.chartData.errorNames?.[code] || code,
          type: 'bar',
          stack: 'total',
          barMaxWidth: 50,
          itemStyle: { color, borderRadius: [4, 4, 0, 0] },
          emphasis: { focus: 'series' },
          data: (this.chartData.errors?.[code] || [])
        })
      }

      // 折线：订正率（右轴，百分比）
      series.push({
        name: '订正率',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: '#1E3A8A' },
        lineStyle: { width: 2, color: '#1E3A8A' },
        data: (this.chartData.fixRates || []).map(v => v == null ? null : Math.round(v * 10000) / 100)
      })

      return series
    },
    updateChart() {
      const el = this.$refs.chart
      if (!el || !this.chart) return
      const w = el.clientWidth
      const h = el.clientHeight
      if (!w || !h) {
        setTimeout(() => this.updateChart(), 120)
        return
      }

      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: function(params) {
            if (!params || !params.length) return ''
            const title = params[0].name || ''
            
            // 获取标签样式函数
            const getTagStyle = (seriesName) => {
              // 检查是否是基础类型
              if (this.baseColors[seriesName]) {
                const color = this.baseColors[seriesName]
                // 将颜色转换为 rgba 格式用于背景
                const hexToRgba = (hex, alpha) => {
                  const r = parseInt(hex.slice(1, 3), 16)
                  const g = parseInt(hex.slice(3, 5), 16)
                  const b = parseInt(hex.slice(5, 7), 16)
                  return `rgba(${r}, ${g}, ${b}, 0.2)`
                }
                const bg = hexToRgba(color, 0.2)
                return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: ${bg}; border: 1px solid ${color}; color: ${color}; line-height: 1.2;`
              }
              
              // 检查是否是错误类型
              const errorCode = this.chartData.errorCodes?.find(code => {
                const errorName = this.chartData.errorNames?.[code] || code
                return errorName === seriesName
              })
              if (errorCode) {
                const style = getErrorTagInlineStyle(errorCode) || {}
                const bgColor = style.backgroundColor || '#1E90FF'
                const borderColor = style.borderColor || bgColor
                const textColor = style.color || bgColor
                // 将背景色转换为半透明
                const hexToRgba = (hex, alpha) => {
                  if (hex.startsWith('rgba')) return hex.replace(/[\d.]+\)$/g, `${alpha})`)
                  if (hex.startsWith('rgb')) return hex.replace('rgb', 'rgba').replace(')', `, ${alpha})`)
                  const r = parseInt(hex.slice(1, 3), 16)
                  const g = parseInt(hex.slice(3, 5), 16)
                  const b = parseInt(hex.slice(5, 7), 16)
                  return `rgba(${r}, ${g}, ${b}, ${alpha})`
                }
                const bg = hexToRgba(bgColor, 0.2)
                return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: ${bg}; border: 1px solid ${borderColor}; color: ${textColor}; line-height: 1.2;`
              }
              
              return null
            }
            
            let content = ''
            let fixRateText = ''
            params.forEach(p => {
              if (p.seriesName === '订正率') {
                fixRateText = `<div>订正率：${p.value == null ? '-' : p.value + '%'}</div>`
              } else if (p.value != null && p.value > 0) {
                const tagStyle = getTagStyle(p.seriesName)
                if (tagStyle) {
                  content += `<div><span style="${tagStyle}">${p.seriesName}</span>：${p.value}</div>`
                } else {
                  content += `<div>${p.seriesName}：${p.value}</div>`
                }
              }
            })
            if (fixRateText) content += fixRateText
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }.bind(this)
        },
        legend: {
          type: 'scroll',
          bottom: 0,
          left: 'center',
          itemGap: 20,
          textStyle: { fontSize: 12 }
        },
        grid: {
          left: '0%',
          right: '0%',
          bottom: '15%',
          top: '30px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.months || [],
          axisLabel: { interval: 0, rotate: 45, fontSize: 12, margin: 8 }
        },
        yAxis: [
          {
            type: 'value',
            name: '题目数量',
            minInterval: 1,
            position: 'left',
            axisLabel: { formatter: '{value}', margin: 8 },
            nameTextStyle: { padding: [0, 15, 0, 20] },
            splitLine: { show: true, lineStyle: { type: 'dashed' } }
          },
          {
            type: 'value',
            name: '订正率',
            min: 0,
            max: 100,
            interval: 20,
            position: 'right',
            offset: 15,
            axisLabel: { formatter: '{value}%', margin: 8, align: 'right' },
            nameTextStyle: { padding: [0, -7, 0, 20], align: 'right' },
            splitLine: { show: false }
          }
        ],
        series: this.buildSeries()
      }

      this.chart.setOption(option, true)
    },
    // 导出与全屏
    handleExport() {
      try {
        const url = this.chart?.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const a = document.createElement('a')
        a.href = url
        a.download = '错题趋势分布.png'
        a.click()
      } catch (e) {}
    },
    handleFullscreen() {
      this.dialogVisible = true
      this.$nextTick(() => {
        this.fullscreenChart = echarts.init(this.$refs.fullscreenChart)
        const option = this.chart?.getOption() || {}
        this.fullscreenChart.setOption(option, true)
        setTimeout(() => { this.fullscreenChart && this.fullscreenChart.resize() }, 0)
      })
    },
    handleDialogOpen() {},
    handleDialogClose() {},
    handleDialogClosed() {
      try { this.fullscreenChart && this.fullscreenChart.dispose() } catch (e) {}
      this.fullscreenChart = null
    },
    handleExportFullscreen() {
      try {
        const url = this.fullscreenChart?.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const a = document.createElement('a')
        a.href = url
        a.download = '错题趋势分布-全屏.png'
        a.click()
      } catch (e) {}
    },
    closeDialog() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.question-wrong-trend {
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}
.title {
  font-weight: normal;
  color: #ffffff;
}
.operations { display: flex; gap: 8px; align-items: center; }
.operations .el-button { color: #ffffff; }
.operations .el-button:hover { color: #BD6C73; }

.chart-container {
  position: relative;
  width: 100%;
  height: 400px;
  padding: 20px;
  box-sizing: border-box;
}
.chart-content .y-axis-label {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 12px;
  color: #909399;
}
.chart-content .y-axis-label.right {
  right: 8px;
  left: auto;
}
.el-button [class^="el-icon-"] { font-size: 16px; }

.chart-dialog :deep(.el-dialog__header) { padding: 0; margin: 0; }
.chart-dialog :deep(.el-dialog__headerbtn) { display: none; }
.chart-dialog :deep(.el-dialog__body) { padding: 0; display: flex; align-items: center; justify-content: center; }
.dialog-header { display: flex; align-items: center; justify-content: space-between; padding: 17px 20px; height: 16px; box-sizing: content-box; background-color: #2E365A; color: #ffffff; border-bottom: none; }
.dialog-operations { display: flex; gap: 8px; }
.dialog-operations .el-button { color: #ffffff; }
.dialog-operations .el-button:hover { color: #BD6C73; }

.fullscreen-chart { width: 100%; height: 600px; padding: 20px; box-sizing: border-box; display: flex; align-items: center; justify-content: center; }
</style>
